/**
 * KpiTile and AttainmentBar — small compositions that lead with the number and
 * carry the grammar through Metric (UX-004 §5: lead with the value).
 */
import type { ReactNode } from "react";
import type { Figure } from "@solarkey/api-client";
import { Metric } from "./metric";
import { VariantChips, Sparkline, type VariantOption } from "./atoms";

/**
 * A KPI tile: label, the figure (via Metric), an optional variant selector
 * (e.g. the four PR variants) and an optional sparkline. The plain-language
 * meaning sits beside the technical term for the five-second read (UX-003 §16).
 */
export function KpiTile(props: {
  label: string;
  figure: Figure;
  plain?: string;
  variants?: VariantOption[];
  selectedVariant?: string;
  onSelectVariant?: (key: string) => void;
  variantName?: string;
  trend?: number[];
  showCoverage?: boolean;
}): ReactNode {
  const selectedLabel = props.variants?.find(
    (v) => v.key === props.selectedVariant,
  )?.label;
  return (
    <div className="sk-tile">
      <div className="sk-tile__head">
        <span className="sk-tile__label">{props.label}</span>
        {props.plain ? <span className="sk-tile__plain">{props.plain}</span> : null}
      </div>
      <Metric
        figure={props.figure}
        variantName={props.variantName ?? selectedLabel}
        showCoverage={props.showCoverage ?? false}
      />
      {props.trend && props.trend.length > 1 ? (
        <div className="sk-tile__trend">
          <Sparkline points={props.trend} />
        </div>
      ) : null}
      {props.variants && props.selectedVariant && props.onSelectVariant ? (
        <div className="sk-tile__variants">
          <VariantChips
            variants={props.variants}
            selected={props.selectedVariant}
            onSelect={props.onSelectVariant}
            ariaLabel={`${props.label} variant`}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Delivered against a plan, with the plan marker. Plan attainment reuses the
 * status palette by meaning (ahead/behind/at-risk/on-plan) — not a second
 * traffic light (UX-004 §2.2). Pass the computed `status`; this component does
 * not infer it.
 */
export type AttainmentStatus = "ahead" | "on_plan" | "behind" | "at_risk";

export function AttainmentBar(props: {
  valuePct: number;
  planMarkerPct?: number;
  status?: AttainmentStatus;
  label?: string;
}): ReactNode {
  const pct = Math.max(0, Math.min(100, props.valuePct));
  return (
    <div
      className="sk-attainment"
      data-status={props.status ?? "on_plan"}
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={props.label ?? `Attainment ${pct}%`}
    >
      <div className="sk-attainment__track">
        <div className="sk-attainment__fill" style={{ width: `${pct}%` }} />
        {typeof props.planMarkerPct === "number" ? (
          <div
            className="sk-attainment__plan"
            style={{ left: `${Math.max(0, Math.min(100, props.planMarkerPct))}%` }}
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}
